import { gql, useMutation } from '@apollo/client';
import { CheckCircle, Close, SettingsEthernet } from '@mui/icons-material';
import {
  Button,
  CircularProgress,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import { useEventCallback } from '@mui/material/utils';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useDialogs } from '@work4all/components';
import { Divider } from '@work4all/components/lib/dataDisplay/divider/Divider';
import { LabeledInput } from '@work4all/components/lib/input/labeled-input';
import { useHistoryStack } from '@work4all/components/lib/navigation/history-stack';
import {
  EventType,
  sendAmplitudeData,
} from '@work4all/components/lib/utils/amplitude/amplitude';

import { useDataMutation, useDataProvider, useFormPlus } from '@work4all/data';
import { useEntityJsonSchema } from '@work4all/data/lib/json-schema/EntityJsonSchemasContext';

import { ExchangeMailClientConfig } from '@work4all/models/lib/Classes/ExchangeMailClientConfig.entity';
import { ExchangeMailConfigTestResult } from '@work4all/models/lib/Classes/ExchangeMailConfigTestResult.entity';
import { DataRequest } from '@work4all/models/lib/DataProvider';
import { Auth } from '@work4all/models/lib/Enums/Auth.enum';
import { EMode } from '@work4all/models/lib/Enums/EMode.enum';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';
import { ExchangeVersion } from '@work4all/models/lib/Enums/ExchangeVersion.enum';

import { useJSONSchemaResolver } from '@work4all/utils';
import { PathsOf } from '@work4all/utils/lib/paths-of/paths-of';

import { EnumPickerField } from '../../../../../components/entity-picker/EnumPickerField';
import { NavigationOverlayHeader } from '../../../../../components/navigation-overlay-header/NavigationOverlayHeader';
import { ControllerPlus } from '../../../form-plus/controller-plus';
import { useFormContextPlus } from '../../../form-plus/use-form-context-plus';
import { Collapse } from '../../components';
import { useConfirmBeforeCloseMask } from '../../hooks/use-confrm-before-close-mask';

type ExchangeMailClientConfigFormValue = PathsOf<ExchangeMailClientConfig, 2>;

type ExchangeMailClientConfigOverlayControllerProps = {
  amplitudeEntryPoint: string;
};

export function ExchangeMailClientConfigOverlayController(
  props: ExchangeMailClientConfigOverlayControllerProps
) {
  const { amplitudeEntryPoint } = props;

  const { goBack } = useHistoryStack();

  const [mutate] = useDataMutation<
    ExchangeMailClientConfig,
    EMode.upsert,
    {
      attachements;
    }
  >({
    entity: Entities.exchangeMailClientConfig,
    mutationType: EMode.upsert,
    responseData: {
      authMethod: null,
    },
  });

  useEffect(() => {
    sendAmplitudeData(EventType.EditExchangeMailClientConfig, {
      entryPoint: amplitudeEntryPoint,
    });
  }, [amplitudeEntryPoint]);

  const request = useExchangeMailClientConfigRequest();

  const { data } = useDataProvider<ExchangeMailClientConfigFormValue>(request);

  const schema = useEntityJsonSchema(Entities.exchangeMailClientConfig);
  const resolver = useJSONSchemaResolver(schema);
  const form = useFormPlus<ExchangeMailClientConfigFormValue>({
    resolver,
    mode: 'onChange',
    defaultValues: data as ExchangeMailClientConfigFormValue,
    shouldFocusError: false,
    context: {
      schema,
    },
  });

  const { t } = useTranslation();

  useEffect(() => {
    form.reset(data as ExchangeMailClientConfigFormValue);
  }, [data, form]);

  const [loading, setLoading] = useState(false);

  const onSubmit = useEventCallback(
    async (
      values: ExchangeMailClientConfigFormValue,
      closeOnSuccess = true
    ) => {
      setLoading(true);
      await mutate({ ...values, __typename: undefined });
      setLoading(false);

      sendAmplitudeData(EventType.SaveExchangeMailClientConfig, {
        entryPoint: amplitudeEntryPoint,
      });

      if (closeOnSuccess) {
        goBack({ force: true });
      }
    }
  );

  useConfirmBeforeCloseMask(form.formState.isDirty);

  const [testEWS] = useExchangeMailClientConfigTest();

  const dialog = useDialogs();

  const handleTestEWS = useCallback(async () => {
    /**
     * Save before testing values
     */
    await onSubmit(form.getValues(), false);

    const {
      data: { testGeneralEwsSettings: result },
    } = await testEWS();
    dialog.alert({
      title: t('COMMON.EWS.TEST_CONNECTION'),
      description: result.connectionOk ? (
        <Stack direction="row" gap="0.5rem">
          <CheckCircle color="success" />
          <Typography>{t('COMMON.EWS.TEST_CONNECTION.OK')}</Typography>
        </Stack>
      ) : (
        <>
          <Stack direction="row" gap="0.5rem">
            <Close color="error" />
            <Typography>{t('COMMON.EWS.TEST_CONNECTION.FAILED')}</Typography>
          </Stack>
          {result.errorMessage?.length > 0 && (
            <>
              <Divider title={t('COMMON.DETAILS')} />
              <Typography>{result.errorMessage}</Typography>
            </>
          )}
        </>
      ),
    });
  }, [dialog, form, onSubmit, t, testEWS]);

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <ExchangeMailClientConfigMaskContent
          loading={loading}
          onTestEWSClick={handleTestEWS}
        />
      </form>
    </FormProvider>
  );
}

function ExchangeMailClientConfigMaskContent(props: {
  loading: boolean;
  onTestEWSClick: () => void;
}) {
  const { t } = useTranslation();

  const { register, control, formState } =
    useFormContextPlus<ExchangeMailClientConfigFormValue>();

  const { loading, onTestEWSClick } = props;

  const { isDirty } = formState;

  return (
    <Stack>
      <NavigationOverlayHeader
        title={t(`COMMON.EXCHANGEMAILCLIENTCONFIGURATION`)}
        breadcrumbsChildren={
          <Stack direction="row" gap="1rem">
            <Button onClick={onTestEWSClick} startIcon={<SettingsEthernet />}>
              {t('COMMON.EWS.TEST_CONNECTION')}
            </Button>
            <Button
              size="large"
              color="primary"
              variant="contained"
              disabled={!isDirty || loading}
              startIcon={
                loading && <CircularProgress color="inherit" size="1rem" />
              }
              type="submit"
            >
              {t('INPUTS.SAVE')}
            </Button>
          </Stack>
        }
        forceStackItems
      />
      <Collapse title={t('COMMON.CONFIGURATION')} defaultOpen>
        <Grid container spacing="1rem">
          <Grid item xs={12} md={12}>
            <LabeledInput label={t('COMMON.EWS.URL')} {...register('ewsUrl')} />
          </Grid>
          <Grid item xs={12} md={4}>
            <ControllerPlus
              control={control}
              name="version"
              render={({ field }) => {
                return (
                  <EnumPickerField
                    values={Object.values(ExchangeVersion)}
                    label={t('COMMON.EWS.SERVER_VERSION')}
                    translationPrefix="EXCHANGE_VERSION"
                    {...field}
                  />
                );
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <LabeledInput
              label={t('COMMON.EWS.USER_NAME')}
              {...register('serviceUserUsername')}
              autoComplete="new-username"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <ControllerPlus
              control={control}
              name="authMethod"
              render={({ field }) => {
                return (
                  <EnumPickerField
                    values={Object.values(Auth)}
                    label={t('COMMON.EWS.AUTH_METHOD')}
                    translationPrefix="EXCHANGE_AUTH"
                    {...field}
                  />
                );
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <LabeledInput
              label={t('COMMON.EWS.CLIENT_ID')}
              {...register('clientId')}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <LabeledInput
              label={t('COMMON.EWS.CLIENT_SECRET')}
              {...register('clientSecret')}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <LabeledInput
              label={t('COMMON.EWS.TENANT_ID')}
              {...register('tenantId')}
            />
          </Grid>
        </Grid>
      </Collapse>
    </Stack>
  );
}

export const useExchangeMailClientConfigRequest = () => {
  return useMemo<DataRequest>(() => {
    const ExchangeMailClientConfigRequestData: ExchangeMailClientConfig<EMode.query> =
      {
        authMethod: null,
        clientId: null,
        clientSecret: null,
        ewsUrl: null,
        serviceUserDomain: null,
        serviceUserPassword: null,
        serviceUserUsername: null,
        tenantId: null,
        version: null,
      };
    const data = ExchangeMailClientConfigRequestData;
    return {
      entity: Entities.exchangeMailClientConfig,
      data,
      completeDataResponse: true,
    };
  }, []);
};

export const useExchangeMailClientConfigTest = () => {
  return useMutation<{ testGeneralEwsSettings: ExchangeMailConfigTestResult }>(
    TEST_EWS
  );
};

const TEST_EWS = gql`
  mutation testEws {
    testGeneralEwsSettings {
      errorMessage
      connectionOk
    }
  }
`;
